import { getDataReaderClient } from "@multiweb/clients/util";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { bridge, createSelectedProfileChangedAction } from "@multiweb/reactCore/bridge";
import { IFormProfile, IRowData, IUser } from "@multiweb/types/contracts";
import { AdditionalOrdering } from "Global/Base/DataSources/Interfaces";
import { DIContainer } from "Global/Base/DIContainer";
import { Event } from "../../../../../Global/Base/Events";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { CancellationToken } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { DeferredStateful, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { EventsController } from "../../../../../Global/Dom/EventsController";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { NullOWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import {
    RequestContextWithoutTransactionContextId,
} from "../../../../../OW.Remoting/Remoting/RequestContextWithoutTransactionContextId";
import { FormProfileBuilder } from "../../DataBuilders/FormProfileBuilder";
import { IProfileData } from "../../DataBuilders/FormProfileDetailBuilder";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { FormManager } from "../../FormManager/IFormManager";
import { FormManagerProperty } from "../../FormManager/IFormManagerInterfaces";
import { ISystemOperationRights, SystemOperation } from "../../Helpers/SystemOperationRights";
import { IFrameProfileInfo } from "../../IFrameColumnInfo";
import { MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { WindowState } from "../../UIManagerInterfaces";
import { Form } from "../Form";
import {
    IFrameWithChildFrames,
    IFrameWithFiltering,
    IFrameWithProfile,
    IFrameWithUniqueIAttribute,
    IMasterableFrame,
} from "../Frames/FrameInterfaces";
import { IForm } from "../IForm";
import { IFrame, IListFrame } from "../IFrame";
import { FormProfileBasicItem } from "./FormProfileBasicItem";
import { FormProfileItem } from "./FormProfileItem";
import { IFormProfileDataContainer } from "./IFormProfileInfo";
import { IFormProfileManager } from "./IFormProfileManager";

const {
    dispatchAction,
} = bridge;

const logger = LazyLoadingSectionLogger.create(LoggingSection.formProfiles);
/** Indikuje pocet filtru, ktere muzou mit stejny nazev + cislo poradi */
const MAX_FILTER_COUNT_WITH_SAME_NAME = 10_000;

/**
 * Umožňuje spravovat profily formulářů (jejich framů a dalších komponent), což jsou rozložení jejich komponent, nastavení sloupců seznamů, velikosti split panelů a pod.
 * @includeToDoc
 */
export class FormProfileManager implements IFormProfileManager {
    /**
     * Událost změny aktuálně vybraného profilu
     */
    public readonly selectedProfileChanged: Event<FormProfileItem, boolean> = new Event<FormProfileItem, boolean>();
    /**
     * Událost změny výchozího profilu
     */
    public readonly defaultProfileChanged: Event<FormProfileItem, boolean> = new Event<FormProfileItem, boolean>();
    /**
     * Událost odstranění profilu
     */
    public readonly profileRemoved: Event<IFormProfileManager, FormProfileItem> = new Event<IFormProfileManager, FormProfileItem>();
    /**
     * Událost aktualizace profilu
     */
    public readonly profileUpdated: Event<IFormProfileManager, FormProfileItem> = new Event<IFormProfileManager, FormProfileItem>();
    /**
     * Událost přidání profilu
     */
    public readonly profileAdded: Event<IFormProfileManager, FormProfileItem> = new Event<IFormProfileManager, FormProfileItem>();
    /**
     * Událost editace profilu
     */
    public readonly profileEdited: Event<IFormProfileManager, FormProfileItem> = new Event<IFormProfileManager, FormProfileItem>();

    private _eventsController = new EventsController();

    /** Umožňuje čekat na načtení profilů při otevírání formuláře */
    private _profilesLoading: DeferredStateful<boolean> = PromiseUtils.deferStateful<boolean>();

    private _profiles: FormProfileItem[] = [];

    private readonly _form: IForm;
    /**
     * Položka "Základní"
     */
    private _basicFormProfileItem: FormProfileItem;
    /**
     * Poslední označený profil
     */
    private _selectedFormProfile: FormProfileItem;
    /**
     * Příznak, zda došlo k označení výchozího profilu po načtení a inicializaci profilů
     */
    private _initialProfileSelected: boolean = false;

    private readonly _currentUserId: number;

    private _canEditImplementationProfiles: boolean = false;

    /**
     * Výchozí implementační profil
     */
    private _defaultImplementationProfile: FormProfileItem = undefined;

    protected _isProfileBeingLoaded: boolean = false;

    public get canEditImplementationProfiles(): boolean {
        return this._canEditImplementationProfiles;
    }

    /**
     * Všechny profily
     */
    public get profiles(): Iterable<FormProfileItem> {
        return this._profileItemsIterator();
    }

    /**
     * Výchozí implementační profil
     */
    public get defaultImplementationProfile(): FormProfileItem | undefined {
        return this._defaultImplementationProfile;
    }

    constructor(form: IForm, { id }: IUser) {
        this._form = form;
        this._currentUserId = id;
        this.selectedProfileChanged.add((profileItem) => {
            if (this._form instanceof Form) {
                this._form.setState(WindowState.favorite, FormManager.instance.getProperty(FormManagerProperty.favoriteForm, { menuId: this._form.menuItemId, formCode: this._form.formName, profileId: profileItem.id }));
            }
            if (profileItem.basic) {
                this.loadBasicProfile();

                this._profilesLoading.resolve();

                return;
            }
            this.loadProfile(profileItem);
            this._profilesLoading.resolve();

            dispatchAction(createSelectedProfileChangedAction(this._form?.formInstanceId, profileItem));
        });
    }
    public get selectedProfile(): FormProfileItem {
        return this._selectedFormProfile;
    }

    public async doesFilterNameExist(profileName: string): Promise<boolean> {
        for (const frame of this._form.frames) {
            if (frame instanceof IListFrame) {
                if (await frame.filterManager?.doesFilterNameExist(profileName)) return true;
            }
        }

        return false;
    }

    private _updateItemSelectionState(profileItem: FormProfileItem, selectedProfileItem: FormProfileItem): void {
        if (profileItem.id !== selectedProfileItem.id)
            profileItem.selected = false;
    }

    private _updateItemDefaultState(profileItem: FormProfileItem, newDefaultProfileItem: FormProfileItem): void {
        if (profileItem.id !== newDefaultProfileItem.id)
            profileItem.default = false;
    }

    private *_profileItemsIterator(): IterableIterator<FormProfileItem> {
        for (const p of this._profiles)
            yield p;
    }

    /**
     * Zaregistruje položce profilu eventy pro správné nastavování označení a výchozího profilu
     */
    private _registerFormProfileItemEvents(formProfileItem: FormProfileItem): void {
        formProfileItem.selectPreview.add(async (previewArgs, _profileItem, settedValue) => {
            if (!previewArgs.cancelEvent && settedValue && this._initialProfileSelected && !(await this._canSelectedItemChange()))
                previewArgs.cancel();
        });

        formProfileItem.selectionChanged.add((profileItem, value) =>
            this._triggerSelectedProfileChanged(profileItem, value),
        );
        formProfileItem.defaultChanged.add((newDefaultProfileItem, value) =>
            this._triggerDefaultProfileChanged(newDefaultProfileItem, value),
        );

        const formProfileItemId = this._getFormProfileId(formProfileItem);
        this._eventsController.addWithKey(formProfileItemId, this.selectedProfileChanged, (selectedProfileItem) => {
            this._updateItemSelectionState(formProfileItem, selectedProfileItem);
        });
        this._eventsController.addWithKey(formProfileItemId, this.defaultProfileChanged, (newDefaultProfileItem) => {
            this._updateItemDefaultState(formProfileItem, newDefaultProfileItem);
        });
    }

    private _triggerSelectedProfileChanged(profileItem: FormProfileItem, value: boolean): void {
        if (profileItem) {
            this.selectedProfileChanged.trigger(profileItem, value);

            if (this._selectedFormProfile)
                this._selectedFormProfile.isEdited = false;

            this._selectedFormProfile = profileItem;
        }
    }

    private _triggerDefaultProfileChanged(newDefaultProfileItem: FormProfileItem, value: boolean): void {
        if (newDefaultProfileItem)
            this.defaultProfileChanged.trigger(newDefaultProfileItem, value);
    }

    // @ts-ignore // nevyužito, ale ponecháno
    private _selectProfile(profile: FormProfileItem): void {
        profile.selected = true;
    }

    public static async getUserFormProfiles(): Promise<IFormProfile[]> {
        const userFormProfiles: IFormProfile[] = [];
        const profiles = await getDataReaderClient().getDataRowsWithParams(
            "UserProfile",
            ["ID", "FormName", "Name"],
            ":UserProfile.ActualUserProfiles() AND (DataType = @0) AND ShowInNavigationTree = @1", //OR DataType = @2 OR DataType = @3 OR DataType = @4
            { params: ["FormProfileMW", true], dataLength: -1 }); //, "GrafyProfile", "GrafySettings", "ModalFormProfile"
        for (const profile of profiles) {
            userFormProfiles.push({ id: AttrValueReader.read(profile["ID"]), title: AttrValueReader.read(profile["Name"]), formCode: AttrValueReader.read(profile["FormName"]), showInNavigationTree: true });
        }

        return userFormProfiles;
    }

    /**
     * Předanému formu vytvoří nový SidePanel s profily
     */
    public async createFromSettingsPanel(): Promise<void> {
        this._form.formSettingsPanel.titleBuilder = new LocalizedStringBuilder("GUI-623204"); // Správa zobrazení
        this._form.formSettingsPanel.contentBuilder = new FormProfileBuilder(this, !this._form.isModal && StringUtils.isNullOrEmpty(this._form.formCondition));
    }

    /**
     * Sestaví datový objekt profilu z jednotlivých framů na formu
     * @param storeFilters Příznak, zda se mají do profilu uložit aktuální filtry framů
     * @param isImplementation Příznak, zda je zobrazení implementační
     * @param existingProfile (Nepovinný) Pokud se sestavuje objekt z již existujícího profilu, předá se zde
     */
    private async _buildFormProfileData(options: {storeFilters: boolean; isImplementation: boolean; profileName: string; existingProfile?: FormProfileItem}): Promise<IFormProfileDataContainer> {
        const data: IFormProfileDataContainer = {
            formProfileData: this._form.profileInfo, // Nastavení, které se týká formuláře jako celku a ne jeho jednotlivých framů
        };

        const {
            storeFilters,
            existingProfile,
            profileName,
            isImplementation,
        } = options;

        for (const frame of this._getAllFramesWithProfiles()) { // Nastavení, které se týká jednotlivých framů formuláře
            let profileInfo = frame.profileInfo;
            profileInfo.attributesToGroup = frame.currentAttributesToGroup;

            if (frame instanceof IMasterableFrame)
                profileInfo.showSubRecords = frame.showSubRecords ?? false;

            if (storeFilters) {
                if (frame instanceof IFrameWithFiltering) {
                    let newFilterId: string | null;

                    if (frame.filterManager) {
                        // Pokud je aktivní filtr, použije se jeho identifikátor, jinak se uloží null jako prázdný filtr ("Vše")
                        if (!frame.filterManager.currentFilter) {
                            newFilterId = null;
                        } else {
                            // Pokud je ukladane zobrazeni implementacni, ale filtr ne, prepneme ho na implementační
                            if (isImplementation && !frame.filterManager.currentFilter.isImplementationFilter) {
                                frame.filterManager.currentFilter.isImplementationFilter = true;
                            }
                            if (!frame.filterManager.currentFilter.filterId) {
                                const originalFilterName = profileName;
                                let ordNumber = 1;
                                let finalFilterName = profileName;
                                do {
                                    if (await frame.filterManager?.doesFilterNameExist(finalFilterName)) {
                                        finalFilterName = `${originalFilterName} ${ordNumber}`;
                                    } else break;
                                } while (ordNumber++ < MAX_FILTER_COUNT_WITH_SAME_NAME);
                                if (await frame.filterManager?.doesFilterNameExist(finalFilterName)) {
                                    MessageWindow.showAlert(new LocalizedStringBuilder("GUI-749397", [ MAX_FILTER_COUNT_WITH_SAME_NAME.toLocaleString() ])); // Počet filtrů v řadě s tímto názvem dosáhl svého maximálního limitu a nebude uložen. (Limit: @0)
                                    return null;
                                }

                                await frame.filterManager.currentFilter.save(finalFilterName, frame.filterManager);
                            } else {
                                await frame.filterManager.currentFilter.save(frame.filterManager.currentFilter.filterName, frame.filterManager);
                            }

                            newFilterId = frame.filterManager.currentFilter.filterId;
                        }
                    } else {
                        // Pokud frame nemá filterManager, je neinicializovaný. Aktuální nastavení filtru bereme z existujícího profilu, pokud existuje.
                        // Na neinicializovaný frame totiž není tato informace propsána.
                        const frameProfileInfo = existingProfile?.data[frame.frameName];
                        if (frameProfileInfo instanceof IFrameProfileInfo) {
                            if (frameProfileInfo.filterId == null) { // Pokud je filtr nedefinovaný nebo prázdný, ukládáme prázdný; ! Dvojité == je tu záměrně
                                newFilterId = null;
                            } else {
                                newFilterId = frameProfileInfo.filterId;
                            }
                        }
                    }

                    profileInfo = {
                        ...profileInfo,
                        filterId: newFilterId,
                    };
                }
            } else {
                profileInfo.filterId = undefined; // JSON.stringify vynechává fieldy s "undefined" - tzn. žádná informace o filtru
            }

            if (frame instanceof IFrameWithUniqueIAttribute) {
                data[frame.uniqueIAttrIdentifier] = profileInfo;
            } else if (frame instanceof IFrame) {
                data[frame.frameName] = profileInfo;
            }
        }

        return data;
    }

    /*
    * Získá všechny framy na formuláři které mají profil (včetně vnořených framů v komponentách TreeFrame a TwoListFrame)
    */
    private _getAllFramesWithProfiles(): IFrameWithProfile[] {
        const frames: IFrameWithProfile[] = [];
        this._form.masterableFrames.forEach(item => {
            if (item instanceof IFrameWithProfile)
                frames.push(item);

            if (item instanceof IFrameWithChildFrames)
                item.childFrames.forEach(child => child instanceof IFrameWithProfile && frames.push(child));
        });
        return frames;
    }

    /**
     * Přednačtení uložených profilů formuláře z DB
     */
    public async preloadSavedProfiles(): Promise<void> {
        this._canEditImplementationProfiles = await DIContainer.instance.create(ISystemOperationRights).get(SystemOperation.EDITIMPPROFILE);

        // "Základní" položka profilu
        const defaultBasicItem = new FormProfileBasicItem(null, await Localization.getTextPromise("GUI-623212"), "", {}, false, false, false, "GUI-623212"); // Základní

        defaultBasicItem.selected = true;
        this._basicFormProfileItem = new FormProfileItem(this._form.formName, defaultBasicItem);
        this._profiles.push(this._basicFormProfileItem);

        const requestContext: RequestContextWithoutTransactionContextId = {
            roles: this._form.ownerForm?.formContext?.roles,
            cancellationToken: CancellationToken.none,
        };
        const formProfiles = await getDataReaderClient().getDataRowsWithParams("UserProfile", ["ID", "Name", "User", "Data", "IsDefault", "ShowInNavigationTree"],
            ":UserProfile.ActualUserProfiles() AND FormName = @0 AND DataType = @1",
            { params: [this._form.formName, "FormProfileMW"], dataLength: -1, orderBy: [`${AdditionalOrdering.linguistic} Name`] },
            undefined, requestContext);

        for (const formProfile of formProfiles) {
            const formProfileJsonData = AttrValueReader.read(formProfile["Data"]);
            const fromProfileParsedData: IFormProfileDataContainer = JSON.parse(formProfileJsonData || null) || {};
            const profileBasicItem = new FormProfileBasicItem(
                AttrValueReader.read(formProfile["ID"]),
                AttrValueReader.read(formProfile["Name"]),
                formProfileJsonData, fromProfileParsedData, AttrValueReader.read(formProfile["IsDefault"]),
                AttrValueReader.read(formProfile["ShowInNavigationTree"]),
                AttrValueReader.read(formProfile["User"]) === 0);
            const formProfileItem = new FormProfileItem(this._form.formName, profileBasicItem);

            this._profiles.push(formProfileItem);
        }
    }

    /**
     * Inicializace profilů
     * @param applyFormProfileID ID profilu, který se má po otevření formuláře aplikovat místo výchozího
     */
    public async initializeProfiles(applyFormProfileID: number | null = null): Promise<boolean> {
        let loadedDefaultItem = this._basicFormProfileItem;
        this._basicFormProfileItem.default = true;

        let userDefaultProfileLoaded = false;

        for (const formProfileItem of this.profiles) {
            this._registerFormProfileItemEvents(formProfileItem);

            if (formProfileItem.default && formProfileItem !== this._basicFormProfileItem) {
                this._basicFormProfileItem.default = false;
            }

            if (formProfileItem.default && formProfileItem.isImplementation) {
                // Uložíme si defaultní implementační profil.
                this._defaultImplementationProfile = formProfileItem;
            }

            // nastavi jako profil, ktery se aplikuje
            if ((applyFormProfileID && (formProfileItem.id === applyFormProfileID)) || (!applyFormProfileID && formProfileItem.default)) {
                if (formProfileItem.isImplementation && userDefaultProfileLoaded) {
                    // V případě, že se jedná o implementační profil a již je načtený defaultní uživatelský profil,
                    // uživatelský profil má přednost a proto zde pokračujeme na další iteraci.
                    continue;
                } else if (formProfileItem !== this._basicFormProfileItem) {
                    // Pokud se jedná o jiný profil než "Základní", uložíme si příznak,
                    // že byl načten uživatelský defaultní profil.
                    userDefaultProfileLoaded = true;
                }
                loadedDefaultItem = formProfileItem;
            }
        }

        // Pokud je defaultní uživatelský profil, u defaultního implementačního zrušíme příznak, že je defaultní.
        if (this._defaultImplementationProfile && loadedDefaultItem !== this._defaultImplementationProfile) {
            this._defaultImplementationProfile.default = false;
        }

        if (loadedDefaultItem === this._basicFormProfileItem) {
            this._profilesLoading.resolve(); // U základního profilu nechceme čekat, není na co a ani nedojde k vyvolání žádných událostí, které by provedly resolve
        }

        loadedDefaultItem.select();
        this._initialProfileSelected = true;
        this._selectedFormProfile = loadedDefaultItem;

        return this._profilesLoading.promise; // Jinak může dojít k situaci, kdy jsou framy inicializovány dříve než data, podle kterých se mají nastavit
    }

    private _isCurrentFilterSaved(): boolean {
        return !!this._form.filterPanel?.currentFilterBlock?.isSaved;
    }

    /**
     * Načte základní profil (implicitní nebo zruší veškeré nastavení profilů)
     */
    public loadBasicProfile(): void {
        this._isProfileBeingLoaded = true;

        this._form.clearProfileInfo(); // Nejdříve je třeba resetovat globální hodnoty formu, které jsou následně použity pro nastavení během téhož na framech (QueryMode)

        for (const frame of this._getAllFramesWithProfiles()) {
            frame.clearProfileInfo();
            if (frame instanceof IFrameWithFiltering && this._isCurrentFilterSaved())
                frame.filterManager?.clearFilter();
        }

        this._basicFormProfileItem.select();
        this._isProfileBeingLoaded = false;
        dispatchAction(createSelectedProfileChangedAction(this._form?.formInstanceId));
    }

    /**
     * Načte výchozí profil
     */
    public loadDefaultProfile(): void {
        for (const profile of this._profiles) {
            if (profile.default) {
                this.loadProfile(profile);
                profile.select();
                return;
            }
        }

        this.loadBasicProfile();
        this._basicFormProfileItem.default = true;
        logger.info("Default profile not found");
    }

    public loadProfileById(id: number): void {
        let profileItem: FormProfileItem = undefined;
        for (const item of this._profiles) {
            if (item.id === id) {
                profileItem = item;
                break;
            }
        }

        this.loadProfile(profileItem);
    }

    /**
     * nacteni profilu formulare
     */
    public loadProfile(profileItem: FormProfileItem): void {
        if (profileItem == undefined) {
            logger.error("Profile not found");
            return;
        }

        this._isProfileBeingLoaded = true;

        if (this._selectedFormProfile && this._selectedFormProfile.isEdited)
            this._selectedFormProfile.discardChanges();

        if (profileItem.basic) {
            this.loadBasicProfile();
            return;
        }

        if (this._selectedFormProfile !== profileItem || !profileItem.selected)
            profileItem.select();

        const framesToLoadProfiles: {
            frame: IFrameWithFiltering;
            filterId: string | undefined | null;
            queryMode: boolean;
        }[] = [];

        try {
            const data: IFormProfileDataContainer = profileItem.data;

            for (const frame of this._getAllFramesWithProfiles()) {
                let profileInfo: IFrameProfileInfo;

                if (frame instanceof IFrameWithUniqueIAttribute) {
                    profileInfo = data[frame.uniqueIAttrIdentifier] as IFrameProfileInfo;
                } else if (frame instanceof IFrame) {
                    profileInfo = data[frame.frameName] as IFrameProfileInfo;
                }

                this._form.profileInfo = data.formProfileData;
                if (profileInfo) {
                    frame.profileInfo = this._processProfileData(profileInfo);

                    if (frame instanceof IFrameWithFiltering) {
                        if (profileItem.storeFilters && profileInfo.filterId) {
                            frame.isInitExecuteFilter = true;
                            if (frame instanceof IFrameWithFiltering) {
                                framesToLoadProfiles.push({ frame, filterId: profileInfo.filterId, queryMode: profileItem.queryMode });
                            }
                        } else {
                            frame.filterManager?.clearFilter();
                        }
                    }
                }
            }

            for (const { frame, filterId, queryMode } of framesToLoadProfiles) {
                frame.initialized.add(() => {
                    frame.loadProfile(filterId, queryMode);
                });
            }
        } catch (ex) {
            logger.error(ex);
        } finally {
            this._isProfileBeingLoaded = false;
        }
    }

    private _processProfileData(data: IFrameProfileInfo): IFrameProfileInfo {
        const orderBy = data.order;
        if (orderBy != null) {
            for (const o of orderBy) {
                // zachování zpětné kompatibility po přejmenování s existujícími profily
                if (Reflect.has(o, "Order") && Reflect.has(o, "AttributeName")) {
                    o.order = (o as any).Order;
                    (o as any).Order = undefined;
                    o.attributeName = (o as any).AttributeName;
                    (o as any).AttributeName = undefined;
                }
            }
        }

        return data;
    }

    /**
     * Zahodí změny a znovu načte aktuální profil
     * @returns Vrací true, pokud znovu načten profil, jinak false
     */
    public async reloadActualProfile(): Promise<boolean> {
        if (await this._form.checkAndSaveForm()) {
            if (this._selectedFormProfile) {
                this.loadProfile(this._selectedFormProfile);
                this._selectedFormProfile.isEdited = false;
                return true;
            } else
                throw new InvalidOperationException("ActualProfile is not set.");
        }
        return false;
    }

    /**
     * Uložení aktuálního profilu formuláře. Vrací true v případě úspěchu
     */
    public async saveCurrentProfile(profile: FormProfileItem = null): Promise<boolean> {
        if (profile.isImplementation && !this._canEditImplementationProfiles)
            return false;

        if (this._selectedFormProfile == null) {
            throw new InvalidOperationException("ActualProfile is not set.");
        }

        UIManager.instance.showLoader();
        try {
            if (profile) {
                if (!await this.isProfileNameUnique(profile.id, profile.title)) { // kontrola, zda je některý z aktuálně použitých filtrů neuložený, a pokud není, tak zkontrolovat, zda již neexistuje takový, který má shodné jméno s názvem ukládaného profilu
                    MessageWindow.showAlert(new LocalizedStringBuilder("GUI-623849")); // Zobrazení s tímto názvem již existuje. Zkuste nějaký jiný.

                    return false;
                }

                if (profile.default) {
                    await this._unsetAllDefaultProfiles();
                }
            }

            if (!profile || (profile && !profile.basic)) {
                const profileObj = await OWManager.getObject(this._selectedFormProfile.id, "UserProfile", undefined);
                const dataContainer: IFormProfileDataContainer = await this._buildFormProfileData({ storeFilters: profile.storeFilters, isImplementation: profile.isImplementation, existingProfile: profile, profileName: profile.title });
                if (dataContainer === null) {
                    return false;
                }
                dataContainer.formProfileData.queryMode = profile.queryMode;
                dataContainer.formProfileData.storeFilters = profile.storeFilters;

                const profileDataJSON = JSON.stringify(dataContainer);
                profileObj.setValue("Data", profileDataJSON);
                profileObj.isChanged = true;
                if (profile) {
                    const user = this._canEditImplementationProfiles && profile.isImplementation ? null : this._currentUserId;
                    profileObj.setValue("User", user);
                    profileObj.setValue("Name", profile.title);
                    profileObj.setValue("IsDefault", profile.default);
                    profileObj.setValue("ShowInNavigationTree", profile.showInNavigationTree);
                }
                await profileObj.applyUpdates();
                this._selectedFormProfile.jsonData = profileDataJSON;
                this._selectedFormProfile.data = dataContainer;
                this._selectedFormProfile.isEdited = false;
                this.profileUpdated.trigger(this, this._selectedFormProfile);
            }
        } finally {
            UIManager.instance.hideLoader();
        }

        return true;
    }

    /**
     * ulozeni profilu formulare
     */
    public async saveNewProfile(profileParams: IProfileData): Promise<FormProfileItem> {
        UIManager.instance.showLoader();
        try {
            const profileExists = await getDataReaderClient().exists("UserProfile",
                ":UserProfile.ActualUserProfiles() AND DB.Upper(FormName) = DB.Upper(@0) AND DataType = @1 AND Name = @2",
                [this._form.formName, "FormProfileMW", profileParams.name]);

            if (profileExists) {
                await MessageWindow.showAlert(new LocalizedStringBuilder("GUI-623849")); // Zobrazení s tímto názvem již existuje. Zkuste nějaký jiný.

                return null;
            }

            if (profileParams.isDefault)
                await this._unsetAllDefaultProfiles();

            const newObj = await OWManager.newObject("UserProfile", undefined);

            const user = this._canEditImplementationProfiles && profileParams.isImplementation ? null : this._currentUserId;
            if (!profileParams.name) {
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-749906")); // Název profilu nesmí být prázdný
                return null;
            }

            const attributes = [
                { attributeName: "User", value: user },
                { attributeName: "Name", value: profileParams.name },
                { attributeName: "IsDefault", value: profileParams.isDefault },
                { attributeName: "FormName", value: this._form.formName },
                { attributeName: "ShowInNavigationTree", value: profileParams.showInNavigationTree },
            ];

            await newObj.setValues(attributes);
            const data = await this._buildFormProfileData({ storeFilters: profileParams.storeFilters, isImplementation: profileParams.isImplementation, profileName: profileParams.name });
            if (data === null) {
                return null;
            }

            data.formProfileData.queryMode = profileParams.queryMode;
            data.formProfileData.storeFilters = profileParams.storeFilters;

            const jsonData = JSON.stringify(data);
            newObj.setValue("Data", jsonData);
            newObj.setValue("DataType", "FormProfileMW");
            await newObj.applyUpdates();

            const newBasicProfileItem = new FormProfileBasicItem(newObj.id, profileParams.name, jsonData, JSON.parse(jsonData || null), profileParams.isDefault, profileParams.showInNavigationTree, !user);
            const newFormProfileItem = new FormProfileItem(this._form.formName, newBasicProfileItem);

            this._registerFormProfileItemEvents(newFormProfileItem);
            newFormProfileItem.select();
            this._profiles.push(newFormProfileItem);
            this.profileAdded.trigger(this, newFormProfileItem);

            if (newFormProfileItem.default)
                this._triggerDefaultProfileChanged(newFormProfileItem, true);

            return newFormProfileItem;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * vycisteni profilu, smaze polozky v hornim menu a v menu stravy profilu
     */
    public clearProfiles(): void {
        // Prazdna metoda
    }

    /**
     * Odstraní profil
     */
    public async removeProfile(profile: FormProfileItem): Promise<boolean> {
        if (profile && !profile.basic) {
            // smazani profilu z oblíbených
            if (this._form instanceof Form) {
                FormManager.instance.setProperty(FormManagerProperty.favoriteForm, { menuId: this._form.menuItemId, formName: this._form.formName, value: false, profile: profile });
            }

            const obj = await OWManager.getObject(profile.id, "UserProfile", undefined);
            await obj.delete();

            const indexToRemove = this._profiles.indexOf(profile);
            const itemToRemove = this._profiles[indexToRemove];
            itemToRemove.remove();
            this._eventsController.removeByKey(this._getFormProfileId(itemToRemove));
            this._profiles.splice(indexToRemove, 1);
            if (itemToRemove.selected) {
                // pokud je mazaný profil vybraný, načte se buď defaultní implementační nebo "Základní".
                if (itemToRemove.default && this.defaultImplementationProfile) {
                    this.defaultImplementationProfile.default = true;
                }
                this.loadDefaultProfile();
            } else {
                if (itemToRemove.default) {
                    // pokud je mazán nevybraný profil a je defaultní, nastaví se jako defaultní buď defaultní implementační nebo "Základní".
                    if (this.defaultImplementationProfile) {
                        this.defaultImplementationProfile.default = true;
                    } else {
                        this._basicFormProfileItem.default = true;
                    }
                }
            }

            this.profileRemoved.trigger(this, profile);
            itemToRemove.dispose(); //dispose musíme volat až po zpracování posluchačů odebrání

            return true;
        }
        return false;
    }

    /**
     * Označí aktuální profil jako upravený (neuložený)
     */
    public setActualProfileEdited(): void {
        if (this._selectedFormProfile)
            this._selectedFormProfile.isEdited = true;
    }

    /**
     * Označí aktuální profil jako neupravený (uložený)
     */
    public setActualProfileUnchanged(): void {
        if (this._selectedFormProfile)
            this._selectedFormProfile.isEdited = false;
    }

    /**
     * Nastaví "Základní" profil jako "Výchozí"
     */
    public setBasicProfileDefault(): void {
        if (this._basicFormProfileItem)
            this._basicFormProfileItem.default = true;
    }

    /**
     * Kontrola rozeditovanosti formuláře a případné zobrazení YNC.
     * Při zrušení/cancelu se zablokuje změna profilu.
     */
    private async _canSelectedItemChange(): Promise<boolean> {
        return await this._form.checkAndSaveForm();
    }

    /**
     * Vrací seznam viditelných a zneviditelněných sloupců daného framu pro předaný nebo výchozí profil přidruženého formuláře
     * @param frameName Název framu
     * @returns Vrací dvě pole názvů viditelných a zneviditelněných sloupců. Nebo vrací null, pokud nebyl nalezen výchozí profil nebo předaný název framu
     */
    public getColumnsVisibilitiesByFrameName(frameName: string, profileId?: number): { visibleColumns: string[]; invisibleColumns: string[] } | null {
        const visibleColumns = [];
        const invisibleColumns = [];
        for (const profileItem of this.profiles) {
            if (((profileId) ? profileItem.id === profileId : profileItem.default) && profileItem.data) {
                if (profileItem.data[frameName] == null)
                    return null;

                const frameColumns = (profileItem.data[frameName] as IFrameProfileInfo).columns;
                if (frameColumns == null)
                    return null;

                for (const columnName of Object.keys(frameColumns)) {
                    const columnValue = (profileItem.data[frameName] as IFrameProfileInfo).columns[columnName];
                    if (columnValue) {
                        if (columnValue.visible)
                            visibleColumns.push(columnName);
                        else if (columnValue.visible === false) { // explicitně kontrola false, null/undefined zahrnovat nechci
                            invisibleColumns.push(columnName);
                        }
                    }
                }

                return { visibleColumns: visibleColumns, invisibleColumns: invisibleColumns };
            }
        }

        return null;
    }

    /**
     * Vrací všechny profily s příznakem IsDefault. Korektně by měl být jen jeden.
     */
    private async _getDefaultProfiles(): Promise<readonly IRowData[]> {
        return await getDataReaderClient().getDataRowsWithParams("UserProfile",
            ["ID", "Name", "Data", "IsDefault", "User"],
            ":UserProfile.ActualUserProfiles() AND DB.Upper(FormName) = DB.Upper(@0) AND DataType = @1 AND IsDefault = @2",
            { params: [this._form.formName, "FormProfileMW", true], dataLength: -1 });
    }

    /**
     * Všem stávajícím profilům s příznakem IsDefault jej odebere (i na DB).
     */
    private async _unsetAllDefaultProfiles(): Promise<void> {
        const defaultProfiles = await this._getDefaultProfiles();
        if (defaultProfiles.length > 0) {
            for (const defaultProfile of defaultProfiles) {
                const profileObject = await OWManager.getObject(defaultProfile.ID.valueOf(), "UserProfile", undefined);
                if ((await profileObject.getValue("User")) instanceof NullOWObject && !this.canEditImplementationProfiles) {
                    // Pokud profil nemá uživatele, jedná se o implementační profil.
                    // Pokud uživatel nemá právo pro editaci implementačních profilů, pokračujeme na další.
                    continue;
                }
                profileObject.isChanged = true;
                profileObject.setValue("IsDefault", false);
                await profileObject.applyUpdates();
            }
        }
    }

    public async isProfileNameUnique(profileId: number, name: string): Promise<boolean> {
        const savedProfiles = await getDataReaderClient().getDataRowsWithParams("UserProfile",
            ["ID", "Name", "Data", "IsDefault"],
            ":UserProfile.ActualUserProfiles() AND NOT(ID = @0) AND Name = @1 AND DB.Upper(FormName) = DB.Upper(@2) AND DataType = @3",
            { params: [profileId, name, this._form.formName, "FormProfileMW"], dataLength: -1 });

        return savedProfiles.length <= 0;
    }

    /**
     * Získá id z položky profilu formuláře a převede ho na string. Podporuje i výchozí položky, které mají id nastavené na null.
     * Pro takové vrací "null".
     * @param formProfileItem
     * @private
     */
    private _getFormProfileId(formProfileItem: FormProfileItem): string {
        return `${formProfileItem.id}`;
    }

    public dispose(): void {
        this._eventsController?.dispose();
        this._profiles?.forEach(profile => profile.dispose());
    }
}
